<template>
    <section class="acc-glass rounded-2xl border border-foxy-border/80 p-5" aria-labelledby="reading-languages-heading">
        <div class="text-sm font-extrabold text-foxy-text">
            <h2 id="reading-languages-heading">Мови читання</h2>
        </div>
        <p id="reading-languages-description" class="mt-2 text-sm text-foxy-text-secondary">
            Обери мови, якими ти читаєш книги. Вони будуть доступні під час ведення історії читання.
        </p>

        <div v-if="loading" class="mt-4 text-sm text-foxy-text-secondary" role="status">Завантажуємо мови читання…</div>

        <div v-else-if="loadError" class="mt-4 rounded-xl border border-red-400/40 bg-red-500/10 p-3" role="alert">
            <p class="text-sm text-red-700 dark:text-red-200">{{ loadError }}</p>
            <button type="button" class="acc-btn mt-3" @click="loadPreferences(true)">Спробувати ще раз</button>
        </div>

        <template v-else>
            <div class="mt-4" role="group" aria-describedby="reading-languages-description reading-languages-history-note">
                <ul class="space-y-2" aria-label="Обрані мови читання" aria-live="polite">
                    <li
                        v-for="language in draft"
                        :key="language.code"
                        class="flex min-h-12 flex-wrap items-center gap-3 rounded-xl border border-foxy-border bg-foxy-surface/70 px-3 py-2"
                    >
                        <span class="min-w-0 flex-1 font-semibold text-foxy-text">{{ language.label }}</span>
                        <span
                            v-if="language.code === 'uk'"
                            class="text-xs font-semibold text-foxy-text-secondary"
                            aria-label="Українська завжди доступна і не може бути видалена"
                        >
                            Завжди доступна
                        </span>
                        <button
                            v-else-if="language.removable !== false"
                            type="button"
                            class="inline-flex min-h-10 min-w-10 items-center justify-center rounded-lg text-foxy-text-secondary transition hover:bg-foxy-elevated hover:text-foxy-text focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                            :aria-label="`Прибрати мову: ${language.label}`"
                            :disabled="saving"
                            @click="removeLanguage(language.code)"
                        >
                            <i class="fas fa-times" aria-hidden="true"></i>
                        </button>
                    </li>
                </ul>

                <div class="mt-3">
                    <button
                        ref="addLanguageButton"
                        type="button"
                        class="acc-btn"
                        :aria-expanded="chooserOpen ? 'true' : 'false'"
                        aria-controls="reading-language-chooser"
                        :disabled="saving || availableLanguages.length === 0"
                        @click="toggleChooser"
                    >
                        <i class="fas fa-plus" aria-hidden="true"></i>
                        <span>Додати мову</span>
                    </button>
                </div>

                <div v-if="chooserOpen" id="reading-language-chooser" class="mt-3 rounded-xl border border-foxy-border bg-foxy-surface p-3">
                    <div class="flex items-end gap-2">
                        <div class="min-w-0 flex-1">
                            <label for="reading-language-search" class="mb-1 block text-xs font-semibold text-foxy-text"> Знайти мову </label>
                            <input
                                id="reading-language-search"
                                ref="languageSearch"
                                v-model="search"
                                type="search"
                                class="foxy-input text-sm"
                                placeholder="Наприклад, Польська"
                                autocomplete="off"
                                role="combobox"
                                aria-autocomplete="list"
                                aria-haspopup="listbox"
                                :aria-expanded="chooserOpen ? 'true' : 'false'"
                                aria-controls="reading-language-results"
                                :aria-activedescendant="activeOptionId"
                                @keydown.down.prevent="moveActiveOption(1)"
                                @keydown.up.prevent="moveActiveOption(-1)"
                                @keydown.enter.prevent="addActiveLanguage"
                                @keydown.esc.prevent.stop="closeChooser(true)"
                            />
                        </div>
                        <button type="button" class="acc-btn min-h-10 !px-3" @click="closeChooser(true)">Закрити</button>
                    </div>

                    <ul
                        v-if="filteredLanguages.length"
                        id="reading-language-results"
                        class="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-lg border border-foxy-border p-1"
                        role="listbox"
                        aria-label="Доступні мови читання"
                    >
                        <li
                            v-for="(language, index) in filteredLanguages"
                            :id="optionId(language)"
                            :key="language.code"
                            class="cursor-pointer rounded-lg px-3 py-2.5 text-sm text-foxy-text transition"
                            :class="index === activeIndex ? 'bg-foxy-elevated' : 'hover:bg-foxy-elevated/70'"
                            role="option"
                            :aria-selected="index === activeIndex ? 'true' : 'false'"
                            @mouseenter="activeIndex = index"
                            @mousedown.prevent
                            @click="addLanguage(language)"
                        >
                            {{ language.label }}
                        </li>
                    </ul>
                    <p v-else class="mt-2 rounded-lg border border-foxy-border p-3 text-sm text-foxy-text-secondary" role="status">
                        Мову не знайдено.
                    </p>
                </div>
            </div>

            <p id="reading-languages-history-note" class="mt-4 text-xs text-foxy-text-secondary">
                Зміни тут не впливають на вже збережені прочитання.
            </p>

            <div v-if="saveError" class="mt-3 text-sm text-red-700 dark:text-red-200" role="alert">
                {{ saveError }}
            </div>
            <div v-if="successMessage" class="mt-3 text-sm text-emerald-700 dark:text-emerald-300" role="status">
                {{ successMessage }}
            </div>

            <button
                ref="saveButton"
                type="button"
                class="acc-btn-primary mt-4 w-full justify-center disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="!dirty || saving"
                @click="savePreferences"
            >
                {{ saving ? 'Збереження…' : 'Зберегти' }}
            </button>
        </template>
    </section>
</template>

<script lang="ts">
import readingLanguagePreferences from '../services/readingLanguagePreferences';

function cloneEntries(entries) {
    return (Array.isArray(entries) ? entries : []).map((entry) => ({ ...entry }));
}

export default {
    name: 'SettingsReadingLanguages',
    data() {
        return {
            loading: true,
            loadError: '',
            saving: false,
            saveError: '',
            successMessage: '',
            snapshot: [],
            draft: [],
            catalog: [],
            chooserOpen: false,
            search: '',
            activeIndex: 0,
        };
    },
    computed: {
        dirty() {
            return this.codesFor(this.draft).join('|') !== this.codesFor(this.snapshot).join('|');
        },
        availableLanguages() {
            const selectedCodes = new Set(this.codesFor(this.draft));
            return this.catalog.filter((language) => !selectedCodes.has(language.code));
        },
        filteredLanguages() {
            const query = this.search.trim().toLocaleLowerCase('uk-UA');
            if (!query) return this.availableLanguages;

            return this.availableLanguages.filter((language) => language.label.toLocaleLowerCase('uk-UA').includes(query));
        },
        activeOptionId() {
            const activeLanguage = this.filteredLanguages[this.activeIndex];
            return activeLanguage ? this.optionId(activeLanguage) : undefined;
        },
    },
    watch: {
        search() {
            this.activeIndex = 0;
        },
        filteredLanguages(languages) {
            if (this.activeIndex >= languages.length) {
                this.activeIndex = Math.max(0, languages.length - 1);
            }
        },
    },
    mounted() {
        this.loadPreferences();
    },
    methods: {
        codesFor(entries) {
            return entries.map((entry) => entry.code);
        },
        applyCanonical(preferences) {
            this.catalog = cloneEntries(preferences.catalog);
            this.snapshot = cloneEntries(preferences.selected);
            this.draft = cloneEntries(preferences.selected);
        },
        async loadPreferences(force = false) {
            this.loading = true;
            this.loadError = '';

            try {
                const preferences = await readingLanguagePreferences.load({ force });
                this.applyCanonical(preferences);
            } catch {
                this.loadError = 'Не вдалося завантажити мови читання. Спробуй ще раз.';
            } finally {
                this.loading = false;
            }
        },
        toggleChooser() {
            if (this.chooserOpen) {
                this.closeChooser(false);
                return;
            }

            this.chooserOpen = true;
            this.search = '';
            this.activeIndex = 0;
            this.$nextTick(() => this.$refs.languageSearch?.focus());
        },
        closeChooser(returnFocus = false) {
            this.chooserOpen = false;
            this.search = '';
            this.activeIndex = 0;

            if (returnFocus) {
                this.$nextTick(() => this.$refs.addLanguageButton?.focus());
            }
        },
        moveActiveOption(direction) {
            const count = this.filteredLanguages.length;
            if (!count) return;

            this.activeIndex = (this.activeIndex + direction + count) % count;
            this.$nextTick(() => {
                document.getElementById(this.activeOptionId)?.scrollIntoView({ block: 'nearest' });
            });
        },
        addActiveLanguage() {
            const language = this.filteredLanguages[this.activeIndex];
            if (language) this.addLanguage(language);
        },
        addLanguage(language) {
            if (this.draft.some((selected) => selected.code === language.code)) return;

            this.draft = this.sortByCatalog([...this.draft, { ...language, removable: language.code !== 'uk' }]);
            this.successMessage = '';
            this.saveError = '';
            this.search = '';
            this.activeIndex = 0;
            this.$nextTick(() => this.$refs.languageSearch?.focus());
        },
        removeLanguage(code) {
            if (code === 'uk' || this.saving) return;

            this.draft = this.draft.filter((language) => language.code !== code);
            this.successMessage = '';
            this.saveError = '';
            this.closeChooser(false);
            this.$nextTick(() => this.$refs.addLanguageButton?.focus());
        },
        sortByCatalog(entries) {
            const catalogOrder = new Map(this.catalog.map((language, index) => [language.code, index]));
            return [...entries].sort((left, right) => {
                if (left.code === 'uk') return -1;
                if (right.code === 'uk') return 1;
                return (catalogOrder.get(left.code) ?? Number.MAX_SAFE_INTEGER) - (catalogOrder.get(right.code) ?? Number.MAX_SAFE_INTEGER);
            });
        },
        validationMessage(error) {
            const errors = error?.response?.data?.errors || {};
            const firstError = Object.values(errors).flat().find(Boolean);
            return firstError || error?.response?.data?.message || 'Не вдалося зберегти мови читання. Спробуй ще раз.';
        },
        async savePreferences() {
            if (!this.dirty || this.saving) return;

            const languageCodes = this.codesFor(this.draft);
            if (!languageCodes.includes('uk')) {
                this.saveError = 'Українська мова має залишатися у списку мов читання.';
                this.successMessage = '';
                return;
            }

            this.saving = true;
            this.saveError = '';
            this.successMessage = '';

            try {
                const preferences = await readingLanguagePreferences.save(languageCodes);
                this.applyCanonical(preferences);
                this.successMessage = 'Мови читання збережено.';
                this.closeChooser(false);
            } catch (error) {
                this.saveError = this.validationMessage(error);
            } finally {
                this.saving = false;
            }
        },
        optionId(language) {
            return `reading-language-option-${language.code}`;
        },
    },
};
</script>
